import { useEffect, useMemo, useRef, useState } from "react";
import type { GetStaticProps } from "next";
import Link from "next/link";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Layout from "../../components/layout";
import {
    KNOWLEDGE_TOPICS,
    knowledgeArticles,
    type KnowledgeArticle,
} from "../../lib/knowledge-articles";
import "../../styles/scss/knowledge.module.scss";

const ALL_TOPICS = "all";
/** Shortest query that opens the typeahead panel. */
const MIN_QUERY = 2;
/** The design shows a short panel; keep it to a glanceable number. */
const MAX_SUGGESTIONS = 6;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Wraps each occurrence of the query so it can be tinted, as in the design. */
const highlight = (text: string, query: string) => {
    const needle = query.trim();
    if (!needle) return text;
    return text
        .split(new RegExp(`(${escapeRegExp(needle)})`, "ig"))
        .map((part, index) =>
            part.toLowerCase() === needle.toLowerCase() ? (
                <mark key={index}>{part}</mark>
            ) : (
                <span key={index}>{part}</span>
            )
        );
};

const PAGE = {
    title: "Knowledge Center",
    description:
        "Middleware's Knowledge Hub offers practical guides and insights to master observability, monitoring, and cloud infrastructure.",
    metaTitle: "Knowledge Center | Middleware",
    metaDescription:
        "Practical guides and insights on observability, monitoring, and cloud infrastructure from Middleware's Knowledge Hub.",
    uri: "/knowledge/",
};

const KnowledgeListingPage = () => {
    const [activeTopic, setActiveTopic] = useState(ALL_TOPICS);
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    /**
     * Grouped by topic in the order KNOWLEDGE_TOPICS defines, so the right-hand
     * column reads in the same order as the sidebar. Topics with no matching
     * article are dropped rather than rendered as an empty heading.
     */
    const groups = useMemo(
        () =>
            KNOWLEDGE_TOPICS.filter(
                (topic) => activeTopic === ALL_TOPICS || topic.slug === activeTopic
            )
                .map((topic) => ({
                    topic,
                    articles: knowledgeArticles.filter(
                        (article) => article.topic === topic.slug
                    ),
                }))
                .filter((group) => group.articles.length > 0),
        [activeTopic]
    );

    /*
     * Search drives the typeahead panel rather than filtering the list below —
     * the design keeps the list intact while suggestions float over it.
     */
    const suggestions = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (needle.length < MIN_QUERY) return [];

        const matches = (article: KnowledgeArticle) =>
            article.title.toLowerCase().includes(needle) ||
            article.description.toLowerCase().includes(needle);

        let budget = MAX_SUGGESTIONS;
        return KNOWLEDGE_TOPICS.map((topic) => {
            if (budget <= 0) return { topic, articles: [] as KnowledgeArticle[] };
            const articles = knowledgeArticles
                .filter((article) => article.topic === topic.slug && matches(article))
                .slice(0, budget);
            budget -= articles.length;
            return { topic, articles };
        }).filter((group) => group.articles.length > 0);
    }, [query]);

    const hasQuery = query.trim().length >= MIN_QUERY;

    /* Close on outside click or Escape, as a typeahead is expected to. */
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!searchRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    return (
        <Layout
            pageTitle={PAGE.metaTitle}
            ogImage={"https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png"}
            uri={PAGE.uri}
            metaTitle={PAGE.metaTitle}
            metaDesc={PAGE.metaDescription}
            metaRobotsNoindex={"index"}
            metaRobotsNofollow={"follow"}
        >
            <div className="knowledge-page">
                <section className="knowledge-hero">
                    {/* Decorative only — the band clips it. */}
                    <div className="knowledge-hero-art" aria-hidden="true">
                        <img className="art-left" src="/images/knowledge/hero-art-left.png" alt="" />
                        <img className="art-right" src="/images/knowledge/hero-art-right.png" alt="" />
                    </div>

                    <div className="knowledge-container">
                        <div className="knowledge-hero-row">
                            <div className="knowledge-hero-copy">
                                <h1>{PAGE.title}</h1>
                                <p>{PAGE.description}</p>
                            </div>

                            <div className="knowledge-search-wrap" ref={searchRef}>
                                <label className="knowledge-search">
                                    <img
                                        src="/images/knowledge/search-icon.svg"
                                        width={20}
                                        height={20}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="search"
                                        value={query}
                                        onChange={(event) => {
                                            setQuery(event.target.value);
                                            setOpen(true);
                                        }}
                                        onFocus={() => setOpen(true)}
                                        placeholder="Search by name, tag, label or annotation"
                                        aria-label="Search the knowledge center"
                                        aria-expanded={open && hasQuery}
                                        role="combobox"
                                        aria-controls="knowledge-suggestions"
                                        autoComplete="off"
                                    />
                                </label>

                                {open && hasQuery && (
                                    <div className="knowledge-suggestions" id="knowledge-suggestions">
                                        {suggestions.length === 0 ? (
                                            <p className="knowledge-suggestions-empty">
                                                No articles match &ldquo;{query.trim()}&rdquo;.
                                            </p>
                                        ) : (
                                            suggestions.map(({ topic, articles }) => (
                                                <div
                                                    className="knowledge-suggestion-group"
                                                    key={topic.slug}
                                                >
                                                    <p className="knowledge-suggestion-label">
                                                        {topic.label}
                                                    </p>
                                                    {articles.map((article) => (
                                                        <Link
                                                            key={article.slug}
                                                            href={`/knowledge/${article.slug}/`}
                                                            className="knowledge-suggestion"
                                                            onClick={() => setOpen(false)}
                                                        >
                                                            <span className="knowledge-suggestion-title">
                                                                {highlight(article.title, query)}
                                                                <img
                                                                    src="/images/knowledge/result-arrow.svg"
                                                                    width={10}
                                                                    height={10}
                                                                    alt=""
                                                                    aria-hidden="true"
                                                                />
                                                            </span>
                                                            <span className="knowledge-suggestion-desc">
                                                                {highlight(article.description, query)}
                                                            </span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="knowledge-body">
                    <div className="knowledge-container">
                        <div className="knowledge-layout">
                            <aside className="knowledge-topics" aria-label="Topics">
                                <button
                                    type="button"
                                    className={activeTopic === ALL_TOPICS ? "is-active" : ""}
                                    onClick={() => setActiveTopic(ALL_TOPICS)}
                                >
                                    All Topics
                                </button>
                                {KNOWLEDGE_TOPICS.map((topic) => (
                                    <button
                                        type="button"
                                        key={topic.slug}
                                        className={activeTopic === topic.slug ? "is-active" : ""}
                                        onClick={() => setActiveTopic(topic.slug)}
                                    >
                                        {topic.label}
                                    </button>
                                ))}
                            </aside>

                            <div className="knowledge-list">
                                {groups.map(({ topic, articles }) => (
                                        <div className="knowledge-group" key={topic.slug}>
                                            <div className="knowledge-badge">{topic.label}</div>

                                            <div className="knowledge-entries">
                                                {articles.map((article) => (
                                                    <Link
                                                        href={`/knowledge/${article.slug}/`}
                                                        className="knowledge-entry"
                                                        key={article.slug}
                                                    >
                                                        <div className="knowledge-entry-head">
                                                            <h2>{article.title}</h2>
                                                            <span
                                                                className="knowledge-entry-arrow"
                                                                aria-hidden="true"
                                                            >
                                                                {/* The chevron asset is white, which only suits the
                                                                    hover state; used as a mask it can take either
                                                                    colour without a second file. */}
                                                                <span className="knowledge-chevron" />
                                                            </span>
                                                        </div>
                                                        <p>{article.description}</p>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
    props: {
        pageTitle: PAGE.metaTitle,
        metaTitle: PAGE.metaTitle,
        description: PAGE.metaDescription,
        uri: PAGE.uri,
        ogImage: "https://wpadmin.middleware.io/wp-content/uploads/2026/10/knowledge-center.webp",
        metaRobotsNoindex: "index",
        metaRobotsNofollow: "follow",
        ...(await serverSideTranslations(
            locale ?? "en",
            // "common" is 586KB and nothing on this page reads it — the text comes
            // from lib/knowledge-articles.ts, and Layout only needs header/footer.
            ["header", "footer"],
            nextI18NextConfig
        )),
    },
});

export default KnowledgeListingPage;
